import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import logo from './assets/brand/pacific-pearl-logo.png';
import posTrainingHero from './assets/trainee/pos-training-hero.png';
import trainingProgressImage from './assets/trainee/training-progress.png';
import './TraineeLanding.css';

const API_BASE = process.env.REACT_APP_TRAINEE_API_BASE || 'https://api.pacificpearltraining.site';
const STORAGE_KEY = 'vasisTraineeLandingSession';
const TRAINING_RESTAURANT_TAG = 'First Wok NY Style';
const TRAINING_RESTAURANT_LABEL = 'First Wok Always';
const TOTAL_TRAINING_STEPS = 5;
const DEFAULT_DAILY_PRACTICE_TARGET = 15;
const DEFAULT_DAILY_PRACTICE_DAYS = 3;
const FEEDBACK_TYPES = [
  'I found an error',
  'I am stuck',
  'Video or page problem',
  'General feedback',
];

const readStoredSession = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
  } catch {
    return null;
  }
};

const saveStoredSession = (session) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
};

const clearStoredSession = () => {
  localStorage.removeItem(STORAGE_KEY);
};

const formatDateTime = (value) => {
  if (!value) return 'Not started';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not started';
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const percent = (completed, target) => {
  if (!target) return 0;
  return Math.max(0, Math.min(100, Math.round((completed / target) * 100)));
};

const fallbackMilestones = [
  { id: 'first-order', target: 1, label: '1 scored order', focus: 'Count checkpoint based only on saved Practice Calls comparisons.' },
  { id: 'ten-orders', target: 10, label: '10 scored orders', focus: 'Count checkpoint based only on saved Practice Calls comparisons.' },
  { id: 'twenty-five-orders', target: 25, label: '25 scored orders', focus: 'Count checkpoint based only on saved Practice Calls comparisons.' },
  { id: 'fifty-orders', target: 50, label: '50 scored orders', focus: 'Count checkpoint based only on saved Practice Calls comparisons.' },
];

const trendLabel = (value, { lowerIsBetter = false } = {}) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return 'No trend yet';
  const numeric = Number(value);
  if (numeric === 0) return 'Steady';
  const isBetter = lowerIsBetter ? numeric < 0 : numeric > 0;
  const sign = numeric > 0 ? '+' : '';
  return `${isBetter ? 'Improved' : 'Needs focus'} ${sign}${numeric}`;
};

const metricValue = (value, fallback = 'N/A') => (
  value === null || value === undefined || Number.isNaN(Number(value)) ? fallback : value
);

const countLabel = (value, singular, plural = `${singular}s`) => {
  const count = Number(value || 0);
  return `${count} ${count === 1 ? singular : plural}`;
};

const penaltyLabel = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return 'N/A';
  return Number(value).toFixed(Number.isInteger(Number(value)) ? 0 : 2);
};

const durationLabel = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return 'Need data';
  const seconds = Number(value) / 1000;
  if (seconds < 10) return `${seconds.toFixed(1)}s`;
  return `${Math.round(seconds)}s`;
};

const rankLabel = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return 'Need data';
  return `#${Math.round(Number(value))}`;
};

function TraineeLanding() {
  const storedSession = useMemo(readStoredSession, []);
  const [email, setEmail] = useState(storedSession?.email || '');
  const [landingData, setLandingData] = useState(null);
  const [formError, setFormError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [isStarting, setIsStarting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [videoReadyToComplete, setVideoReadyToComplete] = useState(false);
  const [maxWatchedSeconds, setMaxWatchedSeconds] = useState(0);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackEmail, setFeedbackEmail] = useState(storedSession?.email || '');
  const [feedbackType, setFeedbackType] = useState(FEEDBACK_TYPES[0]);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackError, setFeedbackError] = useState('');
  const [feedbackStatus, setFeedbackStatus] = useState('');
  const videoRef = useRef(null);

  const progress = landingData?.progress || null;
  const customer = landingData?.customer || null;
  const config = landingData?.config || {};
  const posDestination = config.posUrl || 'https://order-taking-2026-66217e6b27756711050e.o2.myshopify.dev/agent-dashboard';
  const targetOrders = progress?.helperOrders?.target || config.targetOrders || 50;
  const dailyPractice = progress?.dailyPractice || {};
  const dailyPracticeConfig = config.dailyPractice || {};
  const dailyTargetPerDay = dailyPractice.targetPerDay || dailyPracticeConfig.targetPerDay || DEFAULT_DAILY_PRACTICE_TARGET;
  const dailyTargetDays = dailyPractice.targetDays || dailyPracticeConfig.targetDays || DEFAULT_DAILY_PRACTICE_DAYS;
  const completedOrders = progress?.helperOrders?.completed || 0;
  const orderProgressPercent = percent(completedOrders, targetOrders);
  const milestones = config.milestones?.length ? config.milestones : fallbackMilestones;
  const nextMilestone = progress?.guidance?.nextMilestone
    || milestones.find((milestone) => completedOrders < milestone.target)
    || milestones[milestones.length - 1];
  const trend = progress?.guidance?.trend || {};
  const skillMetrics = progress?.skillMetrics || {};
  const requestDetailMetrics = skillMetrics.requestDetail || {};
  const itemCartMetrics = skillMetrics.itemCart || {};
  const speedMetrics = skillMetrics.speed || {};
  const actionMetrics = skillMetrics.actionTelemetry || {};
  const itemFindingMetrics = actionMetrics.itemFinding || {};
  const specialRequestMetrics = actionMetrics.specialRequests || {};
  const cartReviewMetrics = actionMetrics.cartReview || {};
  const nextFocus = skillMetrics.nextFocus;
  const videoComplete = progress?.steps?.video?.status === 'completed';
  const firstOrderComplete = progress?.steps?.pos?.status === 'completed';
  const trainingComplete = completedOrders >= targetOrders;
  const dailyPracticeComplete = dailyPractice.status === 'completed';
  const currentSession = landingData
    ? { email: landingData.customer.email, accessCode: landingData.progress.accessCode }
    : storedSession;
  const trainingRestaurantLabel = config.restaurantLabel || TRAINING_RESTAURANT_LABEL;

  const completedTasks = [
    Boolean(progress),
    videoComplete,
    firstOrderComplete,
    trainingComplete,
    dailyPracticeComplete,
  ].filter(Boolean).length;
  const currentStepNumber = dailyPracticeComplete
    ? TOTAL_TRAINING_STEPS
    : Math.min(completedTasks + 1, TOTAL_TRAINING_STEPS);
  const nextAction = (() => {
    if (!videoComplete) {
      return {
        label: 'Current move',
        title: 'Watch the walkthrough once from start to finish.',
        detail: 'Pay attention to the order rhythm: hear the request, find the item, add special requests, review the cart, then submit.',
        badgeLabel: 'Do now',
        badgeValue: 'Video',
        badgeDetail: 'Unlocks POS practice',
      };
    }

    if (!firstOrderComplete) {
      return {
        label: 'Current move',
        title: 'Create one POS order from start to finish.',
        detail: 'Open the POS in a new tab and complete one full order. Search carefully, add every special request, then check the cart before placing it.',
        badgeLabel: 'Do now',
        badgeValue: '1 order',
        badgeDetail: 'First POS order',
      };
    }

    if (!trainingComplete) {
      const remaining = Math.max(targetOrders - completedOrders, 0);
      return {
        label: 'Practice focus',
        title: nextFocus?.title || 'Take the next Practice Call and copy every detail.',
        detail: nextFocus?.detail || 'Special requests matter most here. Listen for no onion, sauce on side, spice level, sizes, sides, and quantity changes before you place the order.',
        badgeLabel: 'Next checkpoint',
        badgeValue: `${remaining} left`,
        badgeDetail: nextMilestone?.label || '50 scored calls',
      };
    }

    if (!dailyPracticeComplete) {
      const todayRemaining = dailyPractice.today?.remaining ?? dailyTargetPerDay;
      return {
        label: 'Daily practice',
        title: `Complete at least ${dailyTargetPerDay} scored orders today.`,
        detail: 'The goal now is consistency. Show up each day, keep the same order-taking rhythm, and finish the daily minimum before stopping.',
        badgeLabel: 'Today',
        badgeValue: `${Math.max(todayRemaining, 0)} left`,
        badgeDetail: `${dailyPractice.completedDays || 0}/${dailyTargetDays} days complete`,
      };
    }

    return {
      label: 'Next step',
      title: 'Interview review is next.',
      detail: 'Your practice data is ready for review. If something looks wrong or confusing, use Feedback / Help so we can check it.',
      badgeLabel: 'Status',
      badgeValue: 'Ready',
      badgeDetail: 'Interview review',
    };
  })();

  const openFeedback = () => {
    setFeedbackEmail(customer?.email || currentSession?.email || email || '');
    setFeedbackError('');
    setFeedbackStatus('');
    setFeedbackOpen(true);
  };

  const fetchJson = useCallback(async (url, options = {}) => {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || 'Request failed.');
    }
    return payload;
  }, []);

  const refreshProgress = useCallback(async ({ silent = false } = {}) => {
    const session = readStoredSession();
    if (!session?.email) return null;

    if (!silent) {
      setIsRefreshing(true);
      setStatusMessage('');
    }

    try {
      const params = new URLSearchParams({
        email: session.email,
        accessCode: session.accessCode || '',
      });
      const payload = await fetchJson(`${API_BASE}/api/trainee-landing/progress?${params.toString()}`);
      setLandingData(payload);
      saveStoredSession({
        email: payload.customer.email,
        accessCode: payload.progress.accessCode,
      });
      return payload;
    } catch (error) {
      if (!silent) setFormError(error.message);
      return null;
    } finally {
      if (!silent) setIsRefreshing(false);
    }
  }, [fetchJson]);

  useEffect(() => {
    if (storedSession?.email) {
      refreshProgress({ silent: true });
    }
  }, [refreshProgress, storedSession?.email]);

  useEffect(() => {
    if (!landingData) return undefined;
    const intervalId = window.setInterval(() => {
      refreshProgress({ silent: true });
    }, 15000);
    return () => window.clearInterval(intervalId);
  }, [landingData, refreshProgress]);

  const startAccount = async (event) => {
    event.preventDefault();
    setFormError('');
    setStatusMessage('');

    if (!email.trim()) {
      setFormError('Enter the email address you want to use for training.');
      return;
    }
    setIsStarting(true);
    try {
      const payload = await fetchJson(`${API_BASE}/api/trainee-landing/start`, {
        method: 'POST',
        body: JSON.stringify({
          email,
          restaurantTag: TRAINING_RESTAURANT_TAG,
        }),
      });

      setLandingData(payload);
      saveStoredSession({
        email: payload.customer.email,
        accessCode: payload.progress.accessCode,
      });
      setEmail(payload.customer.email);
      setStatusMessage(payload.created
        ? 'Training ID created. When the POS task unlocks, use Open POS here and sign in with this same email.'
        : 'Training ID loaded. Your progress is synced from Shopify.');
    } catch (error) {
      setFormError(error.message);
    } finally {
      setIsStarting(false);
    }
  };

  const recordEvent = async (eventType, payload = {}) => {
    if (!currentSession?.email) return null;

    const result = await fetchJson(`${API_BASE}/api/trainee-landing/events`, {
      method: 'POST',
      body: JSON.stringify({
        email: currentSession.email,
        accessCode: currentSession.accessCode,
        eventType,
        payload,
      }),
    });
    setLandingData(result);
    saveStoredSession({
      email: result.customer.email,
      accessCode: result.progress.accessCode,
    });
    return result;
  };

  const completeVideo = async () => {
    const video = videoRef.current;
    const durationSeconds = video?.duration && Number.isFinite(video.duration) ? video.duration : 0;
    const watchedSeconds = Math.max(maxWatchedSeconds, durationSeconds);

    try {
      setFormError('');
      const result = await recordEvent('video_completed', {
        watchedSeconds,
        durationSeconds,
      });
      const confirmed = await refreshProgress({ silent: true }) || result;
      if (confirmed?.progress?.steps?.video?.status !== 'completed') {
        throw new Error('Video completion was not saved. Please try again.');
      }
      setStatusMessage('Video complete. The POS task is unlocked.');
    } catch (error) {
      setFormError(error.message);
    }
  };

  const openPos = async (event) => {
    event.preventDefault();
    if (!videoComplete) {
      return;
    }

    const posWindow = window.open('about:blank', '_blank');
    try {
      setFormError('');
      const result = await recordEvent('pos_opened');
      const confirmed = await refreshProgress({ silent: true }) || result;
      if (!confirmed?.progress?.steps?.pos?.openedAt) {
        throw new Error('POS open progress was not saved. Please try again.');
      }
      if (posWindow) {
        posWindow.location.href = posDestination;
        posWindow.opener = null;
      } else {
        window.open(posDestination, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      if (posWindow) posWindow.close();
      setFormError(error.message);
    }
  };

  const openOrderHelper = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    if (!firstOrderComplete || !customer?.id) {
      return;
    }

    const targetName = `traineePracticeCalls${Date.now()}`;
    const helperWindow = window.open('', targetName);
    try {
      setFormError('');
      const result = await recordEvent('helper_opened');
      const confirmed = await refreshProgress({ silent: true }) || result;
      if (!confirmed?.progress?.steps?.helper?.openedAt) {
        throw new Error('Practice Calls progress was not saved. Please try again.');
      }
      form.target = targetName;
      form.submit();
    } catch (error) {
      if (helperWindow) helperWindow.close();
      setFormError(error.message);
    }
  };

  const signOutLanding = () => {
    clearStoredSession();
    setLandingData(null);
    setStatusMessage('');
    setFormError('');
    setVideoReadyToComplete(false);
    setMaxWatchedSeconds(0);
  };

  const submitFeedback = async (event) => {
    event.preventDefault();
    setFeedbackError('');
    setFeedbackStatus('');

    if (!feedbackMessage.trim() || feedbackMessage.trim().length < 8) {
      setFeedbackError('Write a short note so we know what to check.');
      return;
    }

    setFeedbackSubmitting(true);
    try {
      await fetchJson(`${API_BASE}/api/trainee-landing/feedback`, {
        method: 'POST',
        body: JSON.stringify({
          email: feedbackEmail || currentSession?.email || email,
          accessCode: currentSession?.accessCode,
          feedbackType,
          message: feedbackMessage,
          pageUrl: window.location.href,
          progress: {
            completedTasks,
            totalTasks: TOTAL_TRAINING_STEPS,
            completedOrders,
            targetOrders,
            latestScore: progress?.stats?.latestScore,
            averageScore: progress?.stats?.averageScore,
          },
        }),
      });
      setFeedbackStatus('Feedback sent. Thank you - we will review it.');
      setFeedbackMessage('');
      window.setTimeout(() => setFeedbackOpen(false), 900);
    } catch (error) {
      setFeedbackError(error.message);
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const onVideoTimeUpdate = () => {
    const video = videoRef.current;
    if (!video) return;
    const nextWatched = Math.max(maxWatchedSeconds, video.currentTime || 0);
    setMaxWatchedSeconds(nextWatched);
    if (video.duration && Number.isFinite(video.duration)) {
      setVideoReadyToComplete(nextWatched >= video.duration * 0.96);
    }
  };

  const taskRows = [
    {
      number: '01',
      title: 'Create Training ID',
      status: progress ? 'complete' : 'active',
      detail: progress
        ? `${customer?.email || email} is connected to ${trainingRestaurantLabel}.`
        : 'Enter your email so the system can create your trainee account.',
    },
    {
      number: '02',
      title: 'Watch POS Walkthrough',
      status: videoComplete ? 'complete' : progress ? 'active' : 'locked',
      detail: videoComplete
        ? `Completed ${formatDateTime(progress?.steps?.video?.completedAt)}.`
        : 'Watch the full order-taking walkthrough before opening the POS.',
    },
    {
      number: '03',
      title: 'Create First POS Order',
      status: firstOrderComplete ? 'complete' : videoComplete ? 'active' : 'locked',
      detail: firstOrderComplete
        ? `First order detected ${formatDateTime(progress?.steps?.pos?.firstOrderCompletedAt)}.`
        : 'Open the new POS from this page, sign in with this same email, and submit one order.',
    },
    {
      number: '04',
      title: 'Complete 50 Practice Calls',
      status: trainingComplete ? 'complete' : firstOrderComplete ? 'active' : 'locked',
      detail: `${completedOrders} of ${targetOrders} scored practice calls completed.`,
    },
    {
      number: '05',
      title: '3-Day Daily Practice',
      status: dailyPracticeComplete ? 'complete' : trainingComplete ? 'active' : 'locked',
      detail: dailyPracticeComplete
        ? '3 daily practice days complete. Interview review is next.'
        : trainingComplete
          ? `${dailyTargetPerDay}+ scored orders each day for ${dailyTargetDays} days.`
          : `Unlocks after you pass the ${targetOrders}-call step.`,
      statusLabel: dailyPracticeComplete ? 'complete' : trainingComplete ? 'next' : 'locked',
    },
  ];

  return (
    <main className="trainee-page">
      <section className="trainee-shell">
        <div className="trainee-brand-row">
          <img src={logo} alt="Pacific Pearl" className="trainee-logo" />
          <div>
            <p className="trainee-kicker">Pacific Pearl training</p>
            <h1>Order-taking readiness</h1>
          </div>
          {landingData && (
            <div className="trainee-header-actions">
              <button type="button" className="text-button" onClick={signOutLanding}>
                Change email
              </button>
            </div>
          )}
        </div>

        <section className="trainee-intro" aria-label="Pacific Pearl training overview">
          <img src={posTrainingHero} alt="" className="trainee-hero-image" aria-hidden="true" />
          <div className="trainee-hero-content">
            <h2>Learn the POS in measured steps.</h2>
            <p>
              This is a remote training process for restaurant order-taking agents. The first
              exercise measures how quickly you learn the POS, how carefully you handle special
              requests, and how your accuracy improves over repeated practice.
            </p>
          </div>
          <div className="trainee-progress-summary">
            <span>Step {currentStepNumber} of {TOTAL_TRAINING_STEPS}</span>
            <div className="summary-track" aria-hidden="true">
              <div style={{ width: `${percent(completedTasks, TOTAL_TRAINING_STEPS)}%` }} />
            </div>
          </div>
        </section>

        <section className="task-grid" aria-label="Training steps">
          {taskRows.map((task) => (
            <article className={`task-tile ${task.status}`} key={task.number}>
              <div className="task-number">{task.number}</div>
              <div>
                <h3>{task.title}</h3>
                <p>{task.detail}</p>
              </div>
              <span className="task-status">{task.statusLabel || task.status}</span>
            </article>
          ))}
        </section>

        <section className="purpose-panel purpose-panel-visual" aria-label="Why 50 orders">
          <div>
            <p className="section-label">Why 50 orders?</p>
            <h2>We measure how quickly you learn accurate order-taking.</h2>
            <p>
              The 50 remote practice calls are how Pacific Pearl evaluates readiness. We are looking
              at accuracy, special-request handling, speed, consistency, and whether your score
              improves as you practice. This helps us understand your potential and decide whether
              you should continue to the next hiring step.
            </p>
          </div>
          <img src={trainingProgressImage} alt="" className="purpose-image" aria-hidden="true" />
        </section>

        {!landingData && (
          <form className="trainee-entry-panel" onSubmit={startAccount}>
            <div>
              <label htmlFor="trainee-email">Email</label>
              <input
                id="trainee-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
              />
            </div>
            <div>
              <label htmlFor="trainee-restaurant">Training restaurant</label>
              <input
                id="trainee-restaurant"
                value={trainingRestaurantLabel}
                readOnly
                aria-readonly="true"
              />
            </div>
            <button type="submit" disabled={isStarting}>
              {isStarting ? 'Loading training...' : 'Start or Continue Training'}
            </button>
          </form>
        )}

        {(formError || statusMessage) && (
          <div className={formError ? 'trainee-alert error' : 'trainee-alert'}>
            {formError || statusMessage}
          </div>
        )}

        {landingData && (
          <>
            <section className="video-workspace" aria-labelledby="training-video-title">
              <div className="video-copy">
                <p className="section-label">Task 02</p>
                <h2 id="training-video-title">Watch the POS walkthrough</h2>
                <p>
                  This video shows the complete process for taking an order in the POS,
                  from finding items to reviewing special requests and submitting the order.
                </p>
              </div>
              <div className="video-frame">
                {config.videoUrl ? (
                  <video
                    ref={videoRef}
                    controls
                    playsInline
                    preload="metadata"
                    src={config.videoUrl}
                    onTimeUpdate={onVideoTimeUpdate}
                    onEnded={() => setVideoReadyToComplete(true)}
                  />
                ) : (
                  <div className="video-placeholder">
                    <span>Training video pending</span>
                    <p>The walkthrough will appear here as soon as the training video is ready.</p>
                  </div>
                )}
              </div>
              <div className="video-actions">
                <button
                  type="button"
                  onClick={completeVideo}
                  disabled={videoComplete || !config.videoUrl || !videoReadyToComplete}
                >
                  {videoComplete ? 'Video Complete' : 'Complete Video Task'}
                </button>
              </div>
            </section>

            {trainingComplete && (
              <section className="completion-panel" aria-label="Training complete">
                <p className="section-label">Training run complete</p>
                <h2>Congratulations, you finished the 50 calls.</h2>
                <p>
                  We will use your scores, timing, and special-request accuracy to understand your
                  potential and decide whether you will continue to the next step. Your next step is
                  the 3-day daily practice run, with at least 15 scored orders each day.
                </p>
              </section>
            )}

            {trainingComplete && (
              <section className="daily-training-panel" aria-label="Step 05 daily practice">
                <div className="daily-training-copy">
                  <p className="section-label">Step 05</p>
                  <h2>Continue with 3 days of daily practice.</h2>
                  <p>
                    Complete at least {dailyTargetPerDay} scored orders each day for {dailyTargetDays} days.
                    This shows whether you can stay consistent and on time after the first 50-call
                    readiness run.
                  </p>
                  <p>
                    After the {dailyTargetDays} days are completed, Pacific Pearl sends a $5
                    appreciation payment for the time spent practicing. If the results look good, the
                    next step is the interview, then real training.
                  </p>
                </div>
                <div className="daily-training-card">
                  <div className="daily-training-stats">
                    <div>
                      <span>Daily minimum</span>
                      <strong>{dailyTargetPerDay}</strong>
                    </div>
                    <div>
                      <span>Days needed</span>
                      <strong>{dailyPractice.completedDays || 0}/{dailyTargetDays}</strong>
                    </div>
                    <div>
                      <span>Today</span>
                      <strong>{dailyPractice.today?.completed || 0}/{dailyTargetPerDay}</strong>
                    </div>
                  </div>
                  <form
                    className="daily-training-form"
                    action={`${API_BASE}/login`}
                    method="post"
                    target="_blank"
                    onSubmit={openOrderHelper}
                  >
                    <input type="hidden" name="shopifyId" value={customer?.id || ''} />
                    <input type="hidden" name="tags" value={JSON.stringify(customer?.tags || [])} />
                    <input type="hidden" name="redirect" value="true" />
                    <button type="submit" disabled={!customer?.id}>
                      Start Daily Practice Calls
                    </button>
                  </form>
                </div>
              </section>
            )}

            <section className="next-focus" aria-label="Next practice focus">
              <div>
                <p className="section-label">{nextAction.label}</p>
                <h2>{nextAction.title}</h2>
                <p>
                  {nextAction.detail}
                </p>
              </div>
              <div className="focus-card">
                <span>{nextAction.badgeLabel}</span>
                <strong>{nextAction.badgeValue}</strong>
                <p>{nextAction.badgeDetail}</p>
              </div>
            </section>

            <section className="practice-console">
              <div className="console-main">
                <p className="section-label">Practice run</p>
                <h2>{completedOrders} / {targetOrders} scored orders</h2>
                <div className="order-track" aria-label={`${orderProgressPercent}% complete`}>
                  <div style={{ width: `${orderProgressPercent}%` }} />
                </div>
                <div className="metric-row">
                  <div>
                    <span>Average score</span>
                    <strong>{progress?.stats?.averageScore ?? 'N/A'}</strong>
                  </div>
                  <div>
                    <span>Latest score</span>
                    <strong>{progress?.stats?.latestScore ?? 'N/A'}</strong>
                  </div>
                  <div>
                    <span>Best score</span>
                    <strong>{progress?.stats?.bestScore ?? 'N/A'}</strong>
                  </div>
                </div>
                <p className="data-source-note">
                  Average, latest, and best scores are calculated from saved order comparison scores.
                  Checkpoint counts are completed scored orders.
                </p>
                <div className="milestone-rail" aria-label="Practice milestones">
                  {milestones.map((milestone) => {
                    const milestonePercent = percent(Math.min(completedOrders, milestone.target), milestone.target);
                    const status = completedOrders >= milestone.target
                      ? 'complete'
                      : completedOrders > 0 && completedOrders < milestone.target
                        ? 'active'
                        : 'pending';
                    return (
                      <div className={`milestone ${status}`} key={milestone.id || milestone.target}>
                        <div className="milestone-topline">
                          <span>{milestone.label}</span>
                          <strong>{Math.min(completedOrders, milestone.target)}/{milestone.target}</strong>
                        </div>
                        <div className="milestone-track" aria-label={`${milestonePercent}% of ${milestone.label}`}>
                          <div style={{ width: `${milestonePercent}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="console-actions">
                <a
                  href={videoComplete ? posDestination : undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-disabled={!videoComplete}
                  className={!videoComplete ? 'disabled-action' : undefined}
                  onClick={openPos}
                >
                  Open POS
                </a>
                <form
                  className="console-action-form"
                  action={`${API_BASE}/login`}
                  method="post"
                  target="_blank"
                  onSubmit={openOrderHelper}
                >
                  <input type="hidden" name="shopifyId" value={customer?.id || ''} />
                  <input type="hidden" name="tags" value={JSON.stringify(customer?.tags || [])} />
                  <input type="hidden" name="redirect" value="true" />
                  <button type="submit" disabled={!firstOrderComplete || !customer?.id}>
                    {trainingComplete ? 'Start Daily Practice' : 'Start Practice Calls'}
                  </button>
                </form>
                <button type="button" className="secondary-action" onClick={() => refreshProgress()} disabled={isRefreshing}>
                  {isRefreshing ? 'Refreshing...' : 'Refresh Progress'}
                </button>
              </div>
            </section>

            <section className="skill-metrics-panel" aria-label="Measured training metrics">
              <div className="skill-metrics-heading">
                <div>
                  <p className="section-label">Action coaching</p>
                  <h2>Use this to improve the next call.</h2>
                  <p>
                    We look at the order result and the POS actions that led there: how quickly
                    you find items, how smoothly you add special requests, and whether the cart is
                    reviewed before placing the order.
                  </p>
                </div>
                <div className="tracking-source-pill">
                  {actionMetrics.trackedOrders
                    ? `${actionMetrics.trackedOrders} order${actionMetrics.trackedOrders === 1 ? '' : 's'} with action timing`
                    : 'Action timing starts after Practice Calls'}
                </div>
              </div>
              <div className="skill-metric-grid">
                <div>
                  <span>Order accuracy</span>
                  <strong>{metricValue(skillMetrics.accuracy?.averageScore)}</strong>
                  <p>{countLabel(skillMetrics.accuracy?.scoredOrders || completedOrders, 'scored order')}</p>
                </div>
                <div>
                  <span>Item search speed</span>
                  <strong>{durationLabel(itemFindingMetrics.averageSelectionElapsedMs)}</strong>
                  <p>Latest {durationLabel(itemFindingMetrics.latestSelectionElapsedMs)} · avg rank {rankLabel(itemFindingMetrics.averageSelectedResultRank)}</p>
                </div>
                <div>
                  <span>Special request speed</span>
                  <strong>{durationLabel(specialRequestMetrics.averageSelectionElapsedMs)}</strong>
                  <p>{metricValue(specialRequestMetrics.zeroResultCount, 0)} no-result search{Number(specialRequestMetrics.zeroResultCount || 0) === 1 ? '' : 'es'}</p>
                </div>
                <div>
                  <span>Cart review time</span>
                  <strong>{durationLabel(cartReviewMetrics.averageSubmitElapsedMs)}</strong>
                  <p>{metricValue(cartReviewMetrics.submittedCount, 0)} submitted cart{Number(cartReviewMetrics.submittedCount || 0) === 1 ? '' : 's'} tracked</p>
                </div>
              </div>
              <div className="skill-metric-grid secondary-metrics">
                <div>
                  <span>Special-request mistakes</span>
                  <strong>{metricValue(requestDetailMetrics.mistakeCount, 0)}</strong>
                  <p>{countLabel(requestDetailMetrics.affectedOrders, 'order')} affected</p>
                </div>
                <div>
                  <span>Item/cart mistakes</span>
                  <strong>{metricValue(itemCartMetrics.mistakeCount, 0)}</strong>
                  <p>{countLabel(itemCartMetrics.affectedOrders, 'order')} affected</p>
                </div>
                <div>
                  <span>POS action events</span>
                  <strong>{metricValue(actionMetrics.eventCount, 0)}</strong>
                  <p>{countLabel(actionMetrics.trackedOrders, 'tracked order')}</p>
                </div>
                <div>
                  <span>Score time penalty</span>
                  <strong>{penaltyLabel(speedMetrics.averageTimePenalty)}</strong>
                  <p>Lower is better</p>
                </div>
              </div>
              {nextFocus && (
                <div className="measured-focus">
                  <span>Next practice focus</span>
                  <strong>{nextFocus.title}</strong>
                  <p>{nextFocus.detail}</p>
                </div>
              )}
            </section>

            <section className="trend-panel" aria-label="Practice trend">
              <div>
                <span>Recent 5-order avg</span>
                <strong>{trend.latestFiveScoreAverage ?? 'Need 5'}</strong>
              </div>
              <div>
                <span>Previous 5-order avg</span>
                <strong>{trend.previousFiveScoreAverage ?? 'Need 10'}</strong>
              </div>
              <div>
                <span>Accuracy trend</span>
                <strong>{trend.scoreDelta === null || trend.scoreDelta === undefined ? 'Need 10 orders' : trendLabel(trend.scoreDelta)}</strong>
              </div>
              <div>
                <span>Time penalty trend</span>
                <strong>
                  {trend.timePenaltyDelta === null || trend.timePenaltyDelta === undefined
                    ? 'Need 10 orders'
                    : trendLabel(trend.timePenaltyDelta, { lowerIsBetter: true })}
                </strong>
              </div>
            </section>

            <section className="recent-orders">
              <div>
                <p className="section-label">Latest scored orders</p>
                <h2>Progress history</h2>
              </div>
              <div className="order-list">
                {(progress?.orders || []).slice(0, 8).map((order) => (
                  <div className="order-row" key={order.traineeOrderId || order.traineeOrderName}>
                    <span>{order.traineeOrderName || `Order ${order.traineeOrderId}`}</span>
                    <span>{formatDateTime(order.traineeOrderCreatedAt)}</span>
                    <strong>{order.score ?? 'N/A'}</strong>
                    {Number(order.mistakeCount || 0) > 0 && (
                      <em>{order.mistakeCount} {order.mistakeCount === 1 ? 'mistake' : 'mistakes'}</em>
                    )}
                  </div>
                ))}
                {(progress?.orders || []).length === 0 && (
                  <div className="order-empty">
                    Scored practice calls will appear here after the first comparison.
                  </div>
                )}
              </div>
            </section>
          </>
        )}
      </section>
      <button
        type="button"
        className="floating-help-button"
        onClick={openFeedback}
        aria-label="Open feedback and help"
      >
        <span className="floating-help-icon" aria-hidden="true">?</span>
        <span className="floating-help-copy">
          <strong>Need help?</strong>
          <span>Feedback / Help</span>
        </span>
      </button>

      {feedbackOpen && (
        <div className="feedback-overlay" role="dialog" aria-modal="true" aria-labelledby="feedback-title">
          <form className="feedback-modal" onSubmit={submitFeedback}>
            <div className="feedback-modal-header">
              <div>
                <p className="section-label">Feedback / Help</p>
                <h2 id="feedback-title">Tell us what happened</h2>
              </div>
              <button type="button" className="feedback-close" onClick={() => setFeedbackOpen(false)}>
                Close
              </button>
            </div>
            <label>
              Email
              <input
                type="email"
                value={feedbackEmail}
                onChange={(event) => setFeedbackEmail(event.target.value)}
                placeholder="you@example.com"
              />
            </label>
            <label>
              Type
              <select value={feedbackType} onChange={(event) => setFeedbackType(event.target.value)}>
                {FEEDBACK_TYPES.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </label>
            <label>
              Message
              <textarea
                value={feedbackMessage}
                onChange={(event) => setFeedbackMessage(event.target.value)}
                placeholder="Tell us what is wrong, where you got stuck, or what we should improve."
                rows={5}
              />
            </label>
            {feedbackError && <div className="feedback-error">{feedbackError}</div>}
            {feedbackStatus && <div className="feedback-success">{feedbackStatus}</div>}
            <div className="feedback-actions">
              <button type="button" className="secondary-action" onClick={() => setFeedbackOpen(false)}>
                Cancel
              </button>
              <button type="submit" disabled={feedbackSubmitting}>
                {feedbackSubmitting ? 'Sending...' : 'Send Feedback'}
              </button>
            </div>
          </form>
        </div>
      )}
    </main>
  );
}

export default TraineeLanding;
